import React, {
  FC,
  createContext,
  ReactNode,
  useRef,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import { useDispatch, useSelector } from "react-redux";

import { useAppSelector } from "@web-solutions/store/types";

import { PaymentProcessorRef } from "core/payment/payment-processor-provider/components";
import { OneTimePaymentRef } from "core/one-time/constans/types";
import { init } from "core/store/billing/actions";

import {
  billingSelectors,
  selectPaymentProject,
  selectPaymentSystem,
} from "core/store/billing/selectors";

import { profileSelectors } from "core/store/profile/selectors";

import { PaymentUpsale } from "./payments/upsale-payment";
import { PaymentSubscription } from "./payments/subscription-payment";
import { upsaleSelectors } from "core/upsale/store/selectors";
import { routingSelectors } from "core/store/routing/selectors";
import { useStore } from "../../../../apps/virtual-brainary/src/onboarding/store/store";
import { useRemoteConfig } from "core/hooks/use-remote-config";
import { processEmail } from "core/store/profile/actions";
import { reInit as reInitRemoteConfig } from '@web-solutions/core/store/remote-config/actions';
import { Options } from "./components/payment-processor";

export type CallBacksType = {
  onSuccess?: (data: any) => void;
  onSuccessUpsale?: (data: any) => void;
  onError?: (data: any) => void;
  onClose?: (data: any) => void;
};

type PaymentProcessorContextType = {
  ppRef: React.RefObject<PaymentProcessorRef | null>;
  otpRef: React.RefObject<OneTimePaymentRef | null>;
  callbacksRef: React.RefObject<CallBacksType | null>;
  options: Options | null;
  setOptions: (options: Options) => void;
};

export const PaymentProcessorContext =
  createContext<PaymentProcessorContextType | null>(null);

type PropsProvider = {
  children: ReactNode;
};

export const PaymentProcessorProvider: FC<PropsProvider> = ({ children }) => {
  const ppRef = useRef<PaymentProcessorRef | null>(null);
  const otpRef = useRef<OneTimePaymentRef | null>(null);
  const callbacksRef = useRef<CallBacksType | null>({});

  const authorized = useAppSelector(profileSelectors.authorized);
  const isStartUpsaleFlow = useAppSelector(upsaleSelectors.isStartUpsaleFlow);
  const isInitBilling = useAppSelector(billingSelectors.isInitBilling);
  const paymentSystem = useSelector(selectPaymentSystem);
  const paymentProject = useSelector(selectPaymentProject);
  const startRoute = useAppSelector(routingSelectors.startRoute);
  const email = useStore((state) => state.email);
  const [options, setOptions] = useState<Options | null>(null);

  const DEFAULT_EMAIL = "default@voery.app";

  const dispatch = useDispatch();

  const resolvedEmail = email || DEFAULT_EMAIL;

  const params = new URLSearchParams(window.location.search);
  const flowParam = params.get("flow");
  const isSubscriptionFlow = flowParam === "subscription";
  const isChangePaymentMethod = flowParam === "change-payment-method";

  useEffect(() => {
    if (
      paymentSystem &&
      paymentProject &&
      !isInitBilling &&
      resolvedEmail &&
      !isSubscriptionFlow &&
      !isChangePaymentMethod
    ) {
      dispatch(processEmail(resolvedEmail));
      dispatch(reInitRemoteConfig());
      dispatch(init());
    }
  }, [
    dispatch,
    paymentSystem,
    paymentProject,
    isInitBilling,
    resolvedEmail,
    isSubscriptionFlow,
    isChangePaymentMethod
  ]);

  return (
    <PaymentProcessorContext.Provider
      value={{
        ppRef,
        otpRef,
        callbacksRef,
        options,
        setOptions
      }}
    >
      {children}

      {isInitBilling && !isChangePaymentMethod &&
        (isStartUpsaleFlow ? (
          <PaymentUpsale
            localOtpRef={otpRef}
            ppRef={ppRef}
            callbacksRef={callbacksRef}
          />
        ) : (
          <PaymentSubscription localPpRef={ppRef} callbacksRef={callbacksRef} />
        ))}
    </PaymentProcessorContext.Provider>
  );
};

export const usePayment = () => {
  const ctx = useContext(PaymentProcessorContext);

  if (!ctx) {
    throw new Error(
      "usePaymentProcessorRef must be used within PaymentProcessorProvider"
    );
  }

  return ctx;
};
